import React, { useState } from 'react'
import {
  TextField,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  Icon,
  Tooltip,
  Typography,
} from '@mui/material'
import { withStyles } from '@mui/styles'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import { passwordInputStyles } from './password-input.styles'

/**
 * @typedef {object} PasswordInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} [title] - Título que se muestra encima del input.
 * @property {string} [tooltip] - Texto del tooltip de ayuda.
 * @property {string} [value] - El valor actual de la contraseña.
 * @property {string} [placeholder] - Texto de marcador de posición.
 * @property {function(object): void} onChange - Callback que se ejecuta cuando el valor cambia.
 * @property {boolean} [error] - Si es `true`, muestra el input en estado de error.
 * @property {string} [errorMessage] - Mensaje de error que se muestra cuando `error` es `true`.
 * @property {string} [helpMessage] - Mensaje de ayuda que se muestra cuando no hay error.
 */

/**
 * Componente de entrada de contraseña con toggle de visibilidad.
 * 
 * Proporciona un input especializado para contraseñas con:
 * - **Toggle de visibilidad**: Botón para mostrar/ocultar la contraseña
 * - **Validación visual**: Estados de error con mensajes personalizados
 * - **Tooltip de ayuda**: Información contextual opcional
 * - **Mensajes de ayuda**: Texto descriptivo debajo del input
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [password, setPassword] = useState('');
 *   const [error, setError] = useState(false);
 * 
 *   return (
 *     <PasswordInput
 *       title="Contraseña"
 *       value={password}
 *       onChange={(e) => setPassword(e.target.value)}
 *       error={error}
 *       errorMessage="La contraseña debe tener al menos 8 caracteres"
 *       helpMessage="Mínimo 8 caracteres"
 *       tooltip="Usa mayúsculas, minúsculas y números"
 *     />
 *   );
 * };
 * 
 * @param {PasswordInputProps} props - Las props del componente PasswordInput.
 */
export const PasswordInput = withStyles(passwordInputStyles)(({ classes, ...props }) => {
  const [visibility, setVisibility] = useState(false)

  return (
    <FormControl className={classes.root}>
      <div className={classes.title}>
        {props.title}
        {props.tooltip && (
          <span style={{ fontSize: 16, marginLeft: 4 }}>
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                  {props.tooltip}
                </Typography>
              }
            >
              <Icon fontSize="inherit">help_outline</Icon>
            </Tooltip>
          </span>
        )}
      </div>
      <TextField
        {...props}
        type={visibility ? 'text' : 'password'}
        variant="outlined"
        value={props.value || ''}
        placeholder={props.placeholder}
        onChange={props.onChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setVisibility(!visibility)}>
                {visibility ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormHelperText id="my-helper-text" error={props.error}>
        {props.error ? props.errorMessage : props.helpMessage}
      </FormHelperText>
    </FormControl>
  )
})
